import { useCallback, useEffect, useRef } from 'react';
import { useProjectParams } from './useProjectParams';
import {
  MessageFromClientScript,
  MessageToClientScript,
  NextTaskAction,
  UseClientScriptControlProps,
} from './useClientScriptControl.types';
import { getNextTaskAction, isValidMessageSource } from './useClientScriptControl.utils';

export function useClientScriptControl({ onFinished }: UseClientScriptControlProps) {
  const { getParam, data } = useProjectParams();
  const lastActionSent = useRef<NextTaskAction | null>(null);
  const testUrl = getParam('website_testing_url');
  const tasks = data?.tasks;

  const getNextTask = useCallback(
    ({ taskDoneUuid }: { taskDoneUuid?: string }) => {
      const taskDoneIndex = tasks?.findIndex((task) => task.task_uuid === taskDoneUuid) || 0;
      return tasks?.[taskDoneIndex + 1];
    },
    [tasks],
  );

  const remoteMessageHandler = useCallback(
    async (e: MessageEvent) => {
      if (!e.data?.ut_done || !isValidMessageSource(e.origin, testUrl)) {
        return;
      }

      const { ut_task_uuid } = e.data as MessageFromClientScript;
      const { source, origin } = e;

      const nextTask = getNextTask({ taskDoneUuid: ut_task_uuid || '' });

      const nextTaskAction = await getNextTaskAction(nextTask);

      (source as Window)?.postMessage?.(
        {
          ut_next: nextTaskAction,
        } satisfies MessageToClientScript,
        origin,
      );

      if (nextTaskAction.action === 'finished') {
        onFinished();
      }
      lastActionSent.current = nextTaskAction;
    },
    [getNextTask, onFinished, testUrl],
  );

  useEffect(() => {
    window.addEventListener('message', remoteMessageHandler);

    return () => {
      window.removeEventListener('message', remoteMessageHandler);
    };
  }, [remoteMessageHandler]);

  return { getNextTask, lastActionSent: lastActionSent };
}
